import { useState } from "react";
import { Button, Select, Space, Tag, Typography, theme } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import ConditionBuilder from "./ConditionBuilder";
import { describeCondition, isConditional } from "./conditionText";
import type { FieldRef } from "./types";

const { Text } = Typography;

export type TransitionDraft = {
  from_status: number;
  to_status: number;
  condition: Record<string, any>;
  condition_text?: string;
  allowed_groups: number[];
};

type Props = {
  /** The state these moves lead out of. */
  fromId: number;
  /** Where it can go, already chosen on the state above this. */
  targets: number[];
  nameOf: (id: number) => string;
  transitions: TransitionDraft[];
  onChange: (transitions: TransitionDraft[]) => void;
  groups: Array<{ id: number; name: string }>;
  fields: FieldRef[];
  ops: string[];
  orderOps: string[];
  disabled?: boolean;
};

/**
 * Who may make a lifecycle move, and when.
 *
 * Until this existed, every move a template defined was offered to everybody who
 * could manage the case, unconditionally. Two things clients ask for constantly
 * had nowhere to live: *"only HSE may suspend a permit"* and *"over 50 000 it
 * goes the board route instead"*.
 *
 * **A move with no rule is not listed here.** The absence of a rule is what
 * "anyone, always" means, and drawing a row per edge would turn a six-state
 * lifecycle into thirty rows of nothing — burying the two that matter. Adding a
 * restriction is therefore an explicit act, and removing every part of one
 * removes the row.
 *
 * The two halves fail in opposite directions, deliberately, and the hints say
 * so: a condition that cannot be resolved still OFFERS the move (a typo must
 * never wedge a case), while naming groups closes it to everybody else.
 */
const TransitionRules = ({
  fromId,
  targets,
  nameOf,
  transitions,
  onChange,
  groups,
  fields,
  ops,
  orderOps,
  disabled,
}: Props) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [openTo, setOpenTo] = useState<number | null>(null);

  const ruleFor = (toId: number) =>
    transitions.find((r) => r.from_status === fromId && r.to_status === toId);

  const setRule = (toId: number, patch: Partial<TransitionDraft>) => {
    const existing = ruleFor(toId);
    const merged: TransitionDraft = {
      from_status: fromId,
      to_status: toId,
      condition: {},
      allowed_groups: [],
      ...(existing ?? {}),
      ...patch,
    };
    // An empty rule is no rule. Keeping the row would make "restricted" and
    // "restricted to nothing in particular" look identical on the screen.
    const empty =
      !isConditional(merged.condition) && merged.allowed_groups.length === 0;
    const rest = transitions.filter(
      (r) => !(r.from_status === fromId && r.to_status === toId)
    );
    onChange(empty ? rest : [...rest, merged]);
  };

  if (targets.length === 0) return null;

  return (
    <div>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {t("Who can make each move, and when?")}
      </Text>
      <Space direction="vertical" size={6} style={{ width: "100%", marginTop: 4 }}>
        {targets.map((toId) => {
          const rule = ruleFor(toId);
          const restricted =
            !!rule &&
            (isConditional(rule.condition) || rule.allowed_groups.length > 0);
          const open = openTo === toId || restricted;
          return (
            <div
              key={toId}
              style={{
                padding: 8,
                borderRadius: token.borderRadius,
                border: `1px solid ${
                  restricted ? token.colorWarningBorder : token.colorBorderSecondary
                }`,
                background: restricted
                  ? token.colorWarningBg
                  : token.colorFillQuaternary,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Text style={{ fontSize: 13, flex: 1 }}>
                  → {nameOf(toId)}
                </Text>
                {restricted ? (
                  <Tag icon={<LockOutlined />} color="warning" style={{ marginInlineEnd: 0 }}>
                    {t("Restricted")}
                  </Tag>
                ) : (
                  <Button
                    size="small"
                    type="link"
                    style={{ padding: 0, height: "auto", fontSize: 11 }}
                    disabled={disabled}
                    onClick={() => setOpenTo(open ? null : toId)}
                  >
                    {open ? t("Cancel") : t("Anyone, always — restrict it")}
                  </Button>
                )}
              </div>

              {open && (
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {t("Only these groups can make this move")}
                  </Text>
                  <Select
                    size="small"
                    mode="multiple"
                    allowClear
                    style={{ width: "100%", marginTop: 3 }}
                    placeholder={t("Anyone who can manage the case")}
                    value={rule?.allowed_groups ?? []}
                    disabled={disabled}
                    onChange={(v) => setRule(toId, { allowed_groups: v })}
                    options={groups.map((g) => ({ value: g.id, label: g.name }))}
                  />

                  <Text
                    type="secondary"
                    style={{ fontSize: 11, display: "block", marginTop: 8 }}
                  >
                    {t("...and only when")}
                  </Text>
                  <div style={{ marginTop: 3 }}>
                    <ConditionBuilder
                      value={rule?.condition ?? {}}
                      onChange={(condition) => setRule(toId, { condition })}
                      fields={fields}
                      // A lifecycle move is not inside any step, so no answer is
                      // "given later" from its point of view: every field in the
                      // template is fair game.
                      stageIndex={-1}
                      ops={ops}
                      orderOps={orderOps}
                      disabled={disabled}
                    />
                  </div>

                  {rule && isConditional(rule.condition) && (
                    <Text
                      type="secondary"
                      style={{ fontSize: 11, display: "block", marginTop: 6 }}
                    >
                      {t(
                        "Offered when {{rule}}. A case that does not match is not offered this move at all — and a rule the system cannot work out still offers it, so a mistake here can never leave a case stuck.",
                        { rule: describeCondition(rule.condition, fields, t) }
                      )}
                    </Text>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </Space>
    </div>
  );
};

export default TransitionRules;
